import { inject } from "fw";
import { Store, handle, dispatch } from "fw-state";
import { LogoutAction, StartAction } from "./actions";
import { UserRepository } from "network/user-repository";
import { User } from "models/user";
import { CreateUserAction, UpdateUserAction, UpdatedUserAction } from "forms/user";
import { ResetUserPasswordAction } from "forms/reset-password";
import { FormErrorHandling } from "./error-handling";
import { ConfirmChangeEmailAction } from "forms/confirm-change-email";

const MAX_SKIP = 2000;
const DEFAULT_PAGE_SIZE = 20;

interface UserShape {
    loaded: boolean;
    collection: User[];
    totalCount: number;
    filter: string;
    search: string;
    sort: string;
    selectAll: boolean;

    pageSize: number;
    currentPage: number;
    lastPage: number;
    pageStartNumber: number;
    pageEndNumber: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    canAdvancePage: boolean;
}

export class FetchUsersAction {
    constructor(public refresh: boolean = false) {}
}

export class ResendChangeEmailAction {
    constructor(public id: string) {}
}

export class CancelChangeEmailAction {
    public updated: User = null;
    constructor(public id: string) {}
}

export class FetchUserAction {
    constructor(
        public id: string,
        public refresh: boolean = false
    ) {}
}
export class UpdateUserFiltersAction {
    constructor(
        public filter: string = null,
        public search: string = null,
        public sort: string = null
    ) {}
}
export class PageForwardUserAction {}
export class PageBackwardUserAction {}
export class UpdatePageUserAction {
    constructor(public page: number) {}
}
export class SortUserAction {
    constructor(public sort: string = null) {}
}

export class ResetUserStateAction {
    constructor() {}
}

@inject
export class UserStore extends Store<UserShape> {
    constructor(private network: UserRepository) {
        super();
    }

    defaultState() {
        return {
            loaded: false,
            collection: [],
            totalCount: 0,
            filter: null,
            search: null,
            sort: "-(metaData.DateLastLoginUtc)",
            selectAll: false,

            pageSize: DEFAULT_PAGE_SIZE,
            currentPage: 1,
            lastPage: 1,
            pageStartNumber: 1,
            pageEndNumber: DEFAULT_PAGE_SIZE,
            hasPreviousPage: false,
            hasNextPage: false,
            canAdvancePage: false
        };
    }

    @handle(StartAction)
    @handle(ResetUserStateAction)
    private handleStart(action: StartAction) {
        this.setState((state) => ({ ...this.defaultState() }));
    }

    @handle(LogoutAction)
    private handleLogout() {
        this.setState((state) => this.defaultState());
    }

    @handle(UpdateUserFiltersAction)
    private async handleUpdateUserFilters(action: UpdateUserFiltersAction) {
        this.setState((state) => ({
            ...state,
            currentPage: 1,
            filter: action.filter,
            search: action.search,
            sort: action.sort,
            loaded: false
        }));
        await this.handleFetchUsers(new FetchUsersAction());
    }

    @handle(FetchUsersAction)
    private async handleFetchUsers(action: FetchUsersAction) {
        if (this.state.loaded && !action.refresh) return;

        const { filter, search, sort, pageSize } = this.state;
        let { currentPage } = this.state;

        const res = await this.network.list(filter, search, sort, currentPage, pageSize);
        const totalCount = res.total;

        const lastPage = Math.ceil(totalCount / pageSize);
        currentPage = Math.min(currentPage, lastPage);

        const pageStartNumber = (currentPage - 1) * pageSize + 1;
        const pageEndNumber = pageStartNumber + (pageSize - 1);
        const hasNextPage = pageEndNumber < totalCount;
        const hasPreviousPage = pageStartNumber > 1;
        const canAdvancePage = pageEndNumber + 1 < MAX_SKIP;

        this.setState((state) => ({
            ...state,
            loaded: true,
            collection: res.list,
            totalCount,
            currentPage,
            pageStartNumber: Math.max(pageStartNumber, 0),
            pageEndNumber: Math.min(pageEndNumber, totalCount),
            hasNextPage,
            hasPreviousPage,
            canAdvancePage,
            lastPage
        }));
    }

    @handle(FetchUserAction)
    private async handleFetchUser(action: FetchUserAction) {
        if (!action.id) return;

        const item = this.state.collection.find((i) => i.Id == action.id);
        if (item && !action.refresh) return;

        const newItem = await this.network.get(action.id);
        const collection = this.state.collection.slice();

        if (item) collection[collection.indexOf(item)] = newItem;
        else collection.push(newItem);

        this.setState((state) => ({ ...state, collection: collection }));
    }

    @handle(PageForwardUserAction)
    private handlePageForward(action: PageForwardUserAction) {
        this.setState((state) => ({
            ...state,
            currentPage: state.canAdvancePage ? state.currentPage++ : state.currentPage
        }));
    }

    @handle(PageBackwardUserAction)
    private handlePageBackward(action: PageBackwardUserAction) {
        this.setState((state) => ({
            ...state,
            currentPage: state.currentPage > 0 ? state.currentPage-- : state.currentPage
        }));
    }

    @handle(UpdatePageUserAction)
    private handleUpdatePage(action: UpdatePageUserAction) {
        this.setState((state) => ({
            ...state,
            loaded: false,
            currentPage: action.page > 0 && action.page <= state.lastPage ? action.page : state.currentPage
        }));
    }

    @handle(SortUserAction)
    private handleSortUserAction(action: SortUserAction) {
        this.setState((state) => ({
            ...state,
            loaded: false,
            sort: action.sort
        }));
    }

    @handle(CreateUserAction, FormErrorHandling)
    private async handleCreateUser(action: CreateUserAction) {
        action.form.validate();

        const newItem = await this.network.post(action.form.updatedModel());
        const collection = this.state.collection.slice();

        collection.push(newItem);
        this.setState((state) => ({ ...state, collection: collection }));
        action.created = newItem;
    }

    @handle(UpdateUserAction, FormErrorHandling)
    private async handleUpdateUser(action: UpdateUserAction) {
        action.form.validate();

        const updatedItem = await this.network.put(action.form.updatedModel());
        const collection = this.state.collection.slice();
        const currentItem = collection.find((i) => i.Id == updatedItem.Id);

        collection[collection.indexOf(currentItem)] = updatedItem;
        this.setState((state) => ({ ...state, collection: collection }));
        action.updated = updatedItem;

        dispatch(new UpdatedUserAction(updatedItem));
    }

    @handle(ResetUserPasswordAction, FormErrorHandling)
    private async handleResetUserPassword(action: ResetUserPasswordAction) {
        action.form.validate();
        await this.network.resetPassword(action.form.Password, action.resetToken);
    }

    @handle(ConfirmChangeEmailAction, FormErrorHandling)
    private async handleChangeEmail(action: ConfirmChangeEmailAction) {
        action.form.validate();
        await this.network.confirmChangeEmail(action.form.Password, action.token);
    }

    @handle(ResendChangeEmailAction, FormErrorHandling)
    private async handleResendChangeEmail(action: ResendChangeEmailAction) {
        await this.network.resendChangeEmail(action.id);
    }

    @handle(CancelChangeEmailAction, FormErrorHandling)
    private async handleCancelChangeEmail(action: CancelChangeEmailAction) {
        const updatedItem = await this.network.cancelChangeEmail(action.id);

        const users = [...this.state.collection];
        const currentUser = users.find((i) => i.Id == updatedItem.Id);

        users[users.indexOf(currentUser)] = updatedItem;
        this.setState((state) => ({ ...state, collection: users }));
        action.updated = updatedItem;

        dispatch(new UpdatedUserAction(updatedItem));
    }
}
